import { Button, Divider, Grid, Stack, Typography } from '@mui/material'
import { useAppSelector } from 'app/hooks'
import DialogConfirm from 'components/Dialogs/DialogConfirm'
import FloorMap from 'components/FloorMap'
import OpenStressMap from 'components/Map'
import { format } from 'date-fns'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axiosClient from 'utils/axiosClient'

export default function EventDetail() {
  const { enqueueSnackbar } = useSnackbar()
  const UIPrefectures = useAppSelector((s) => s.UI.prefectures)
  const { id } = useParams<{ id: string }>()
  const [joinConfirm, setJoinConfirm] = useState(false)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const event = useAppSelector((s) =>
    s.Event.list.find((f) => f.id === Number(id))
  )
  if (!event) return null
  const handleSubmit = async () => {
    try {
      if (!event.checkpoints) return null
      const cpc = event.checkpoints.map((y) => [{ cpId: y.id, cleared: 0 }])
      setLoading(true)
      await axiosClient.post(`/api/events/${id}/join`, {
        data: JSON.stringify({ cpCheck: cpc }),
      })
      const msg = 'イベントを開始しました。'
      enqueueSnackbar(msg, {
        variant: 'success',
      })
      setTimeout(() => {
        navigate(`/event/${id}`)
      }, 100)
      setJoinConfirm(false)
    } catch (error) {
      const msg = 'エラーが発生しました。'
      enqueueSnackbar(msg, {
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <DialogConfirm
        open={joinConfirm}
        maxWidth="xs"
        loading={loading}
        handleSubmit={handleSubmit}
        title="確認"
        primaryButtonText="公開"
        primaryButtonColor="primary"
        handleClose={() => setJoinConfirm(false)}
      >
        <Typography>イベントに参加しますか？</Typography>
      </DialogConfirm>
      <Grid container spacing={1} mt={2}>
        <Grid item xs={12}>
          <Typography
            variant="h5"
            fontWeight="bold"
            component="h2"
            sx={{ borderBottom: 2 }}
          >
            イベント詳細
          </Typography>
        </Grid>
        <Grid item xs={12} ml={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Stack ml={1}>
                <Typography variant="h6" component="h3" fontWeight="bold">
                  {event.name}
                </Typography>
                <Typography
                  gutterBottom
                  variant="subtitle1"
                  component="h4"
                  mt={1}
                  fontWeight="bold"
                >
                  基本情報
                </Typography>
                <Typography
                  gutterBottom
                  variant="body1"
                  component="h6"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  開催場所
                </Typography>
                <Typography gutterBottom variant="body1" component="h6" mb={2}>
                  {UIPrefectures.find((f) => f.id === event.prefectureId)
                    ?.name + event.address}
                </Typography>
                <Typography
                  gutterBottom
                  variant="body1"
                  component="h6"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  開催期間
                </Typography>
                <Typography gutterBottom variant="body1" component="h6" mb={2}>
                  {format(event.startDate, 'yyyy/MM/dd')} から{' '}
                  {format(event.endDate, 'yyyy/MM/dd')} まで
                </Typography>
                <Typography
                  gutterBottom
                  variant="body1"
                  component="h6"
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  概要説明文
                </Typography>
                <Typography gutterBottom variant="body1" component="h6">
                  {event.summary}
                </Typography>
              </Stack>
            </Grid>
            <Grid
              item
              xs={12}
              md={5}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <img
                width={300}
                src={event.image}
                alt={event.name}
                loading="lazy"
                crossOrigin="anonymous"
              />
            </Grid>
          </Grid>
          {event.rewardSummary && event.rewardImage ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <Stack ml={1}>
                    <Typography
                      gutterBottom
                      variant="body1"
                      component="h6"
                      sx={{
                        fontWeight: 'bold',
                      }}
                    >
                      クリア特典
                    </Typography>
                    <Typography
                      gutterBottom
                      variant="body1"
                      component="h6"
                      mb={2}
                    >
                      {event.rewardSummary}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={5}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <img
                    width={300}
                    src={event.rewardImage}
                    alt={event.name + event.id}
                    loading="lazy"
                    crossOrigin="anonymous"
                  />
                </Grid>
              </Grid>
            </>
          ) : (
            ''
          )}
        </Grid>
        <Grid item xs={12}>
          {event.useGPS
            ? event.checkpoints.length > 0 && (
                <OpenStressMap positions={event.checkpoints} />
              )
            : event.floors.length > 0 && (
                <FloorMap floors={event.floors} positions={event.checkpoints} />
              )}
        </Grid>
        {event.joins.length === 0 ? (
          <Grid
            item
            xs={12}
            ml={1}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Button
              color="primary"
              variant="contained"
              sx={{
                px: 12,
                borderColor: '#aaa',
                borderRadius: 2,
                mt: 4,
              }}
              onClick={() => setJoinConfirm(true)}
            >
              イベントに参加する
            </Button>
          </Grid>
        ) : (
          <Grid
            item
            xs={12}
            ml={1}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Button
              color="primary"
              variant="contained"
              sx={{
                px: 12,
                borderColor: '#aaa',
                borderRadius: 2,
                mt: 4,
              }}
              onClick={() => navigate(`/event/${id}/game`)}
            >
              ゲームを再開する
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  )
}
